import { defineStore } from "pinia";
import { ref } from "vue";

export type ToastType = "success" | "error" | "warning" | "info";

export interface ToastItem {
    id: number;
    type: ToastType;
    message: string;
    duration: number;
}

const MAX_VISIBLE = 4;
const DEFAULT_DURATION = 5000;

let nextId = 1;

export const useToastStore = defineStore("toast", () => {
    const toasts = ref<ToastItem[]>([]);
    const timers = new Map<number, ReturnType<typeof setTimeout>>();

    const dismiss = (id: number) => {
        const index = toasts.value.findIndex((toast) => toast.id === id);
        if (index !== -1) {
            toasts.value.splice(index, 1);
        }

        const timer = timers.get(id);
        if (timer) {
            clearTimeout(timer);
            timers.delete(id);
        }
    };

    const push = (type: ToastType, message: string, duration: number) => {
        const id = nextId++;

        if (toasts.value.length >= MAX_VISIBLE) {
            dismiss(toasts.value[0].id);
        }

        toasts.value.push({ id, type, message, duration });

        if (duration > 0) {
            timers.set(
                id,
                setTimeout(() => dismiss(id), duration),
            );
        }
    };

    const success = (message: string, duration = DEFAULT_DURATION) =>
        push("success", message, duration);
    const error = (message: string, duration = DEFAULT_DURATION) =>
        push("error", message, duration);
    const warning = (message: string, duration = DEFAULT_DURATION) =>
        push("warning", message, duration);
    const info = (message: string, duration = DEFAULT_DURATION) =>
        push("info", message, duration);

    const clearAll = () => {
        timers.forEach((timer) => clearTimeout(timer));
        timers.clear();
        toasts.value = [];
    };

    return {
        toasts,
        success,
        error,
        warning,
        info,
        dismiss,
        clearAll,
    };
});
